import { IInput, IField } from 'app/services/api5-service/api.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { getInputWidth$ } from 'common/dynamic-form/utils/get-input-width';
import { getDesc } from 'common/dynamic-form/wrappers/field-base/description/description.utils';
import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';
import { DocHelper } from 'utils/dochelper';

import { InputProps } from './model/input-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
  ISPValidator,
} from '../../model';

/**
 * Get stream of prefix msg for input
 *
 * @param control
 * @param state
 */
function getPrefix$(
  control: IInput,
  state: ISPFormState,
): Observable<string | undefined> {
  return state.docSubject.pipe(
    map(doc =>
      control.$prefix === 'yes' ? doc[`${control.$name}_prefix`]?.$ : undefined,
    ),
  );
}

/**
 * Get postfix msg for input
 *
 * @param field
 * @param state
 */
function getPostfix(field: IField, state: ISPFormState): string | undefined {
  const desc = getDesc(field, state)?.msg;
  return desc && desc.length < 5 ? desc : undefined;
}

/**
 * Get stream of postfix msg for input
 *
 * @param field
 * @param state
 */
function getPostfix$(
  field: IField,
  state: ISPFormState,
): Observable<string | undefined> {
  return state.docSubject.pipe(map(() => getPostfix(field, state)));
}

/**
 * Get props for input-text field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getInputTextProps(
  control: IInput,
  field: IField,
  state: ISPFormState,
): InputProps {
  const postfix$ = getPostfix$(field, state);

  return {
    originalControl: control,
    originalField: field,
    fieldSettings$: postfix$.pipe(
      map(descMsg => {
        const fieldSettings: FieldSettings = {
          hideDesc: Boolean(descMsg),
          displayMode: 'input-like',
          tagPlace: 'field',
        };
        return fieldSettings;
      }),
    ),
    prefix$: getPrefix$(control, state),
    postfix$: postfix$,
    unlimited: Boolean(control.$unlimit),
    unlimitedValue: control.$unlimit,
    maxLength: control.$maxlength ? Number(control.$maxlength) : undefined,
    zoomRows: control.$zoom,
    zoomHint$: DocHelper.getMessage$('msg_zoom', state.docSubject),
    placeholder$: state.mixedService.getControlPlaceholder$(
      control,
      ISPFieldType.Input,
    ),
    width$: getInputWidth$(control),
    mask: control.$mask,
    autocomplete: control.$autocomplete || 'new-password',
  };
}

/**
 * Get config for input-text field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getInputTextConfig(
  control: IInput,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Input> {
  const validation: ISPValidator[] = [];
  const canDuplicate = control.$duplicate === 'yes';
  if (control.$zoom && !canDuplicate) {
    validation.push(ISPValidator.Duplicate);
  }

  const props = getInputTextProps(control, field, state);

  const config: ISPFieldConfig<ISPFieldType.Input> = {
    key: control.$name,
    type: ISPFieldType.Input,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
    props,
    validators: {
      validation,
    },
    expressions: {
      'props.hideDesc': props.postfix$.pipe(map(postfix => Boolean(postfix))),
    },
  };

  return config;
}

/**
 * Input elements (input, password, textarea) have special logic for autofocus
 *
 * @param element - focusable element
 * @param config - field config
 * @param formState - form state
 */
export async function setAutofocus(
  element: HTMLInputElement | HTMLTextAreaElement,
  config: ISPFieldConfig,
  formState: ISPFormState,
): Promise<void> {
  await formState.layoutService.scrollToField(config, true);

  element.focus({
    preventScroll: true,
  });
}
